import type { MutableRefObject, RefObject } from "react";
import {
  ContextEditView,
  InlineDiffView,
  TextContentView,
  TextMetricsBar,
} from "../../shared/components";
import type { InlineDiffViewHandle } from "../../shared/components";
import ExplainChangesView from "../ExplainChangesView";
import type {
  ChangeExplanation,
  ComparisonState,
  HistoryEntry,
  ViewMode,
} from "../types";

interface ComparisonPanelProps {
  prompt: string;
  comparisonAction: 'apply' | 'discard' | null;
  comparison: ComparisonState;
  sessionHistory: HistoryEntry[];
  historyIndex: number;
  viewMode: ViewMode;
  useTrackChanges: boolean;
  trackChangesSupported: boolean;
  diffRef: RefObject<InlineDiffViewHandle>;
  explanationCache: MutableRefObject<Map<string, ChangeExplanation[]>>;
  insertReviewComment: boolean;
  onModify: () => void;
  onLoadHistoryEntry: (index: number) => void;
  onViewModeChange: (mode: ViewMode) => void;
  onSelectedSuggestionChange: (index: number) => void;
  onUseTrackChangesChange: (checked: boolean) => void;
  onInsertReviewCommentChange: (checked: boolean) => void;
  onCopyReport: () => void;
  onDiscard: () => void;
  onApply: () => void;
}

const Spinner = () => (
  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const ComparisonPanel = ({
  prompt,
  comparisonAction,
  comparison,
  sessionHistory,
  historyIndex,
  viewMode,
  useTrackChanges,
  trackChangesSupported,
  diffRef,
  explanationCache,
  insertReviewComment,
  onModify,
  onLoadHistoryEntry,
  onViewModeChange,
  onSelectedSuggestionChange,
  onUseTrackChangesChange,
  onInsertReviewCommentChange,
  onCopyReport,
  onDiscard,
  onApply,
}: ComparisonPanelProps) => {
  const isBusy = comparisonAction !== null;
  const selectedSuggestion =
    comparison.suggestions[comparison.selectedSuggestionIndex]?.content || "";

  return (
    <div className="flex flex-col gap-0">
      {/* Instruction bar */}
      <ContextEditView
        label="Your Instruction:"
        content={prompt}
        onClickToModify={isBusy ? undefined : onModify}
      />

      {/* History navigation */}
      {sessionHistory.length > 1 && (
        <div className="flex items-center justify-between px-4 py-1.5 bg-gray-50 border-b border-gray-100">
          <button
            onClick={() => onLoadHistoryEntry(historyIndex + 1)}
            disabled={isBusy || historyIndex >= sessionHistory.length - 1}
            aria-label="Previous history entry"
            className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-brand-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            Older
          </button>
          <span className="text-[10px] text-gray-400 font-medium tabular-nums">
            {historyIndex + 1} / {sessionHistory.length}
          </span>
          <button
            onClick={() => onLoadHistoryEntry(historyIndex - 1)}
            disabled={isBusy || historyIndex <= 0}
            aria-label="Next history entry"
            className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-brand-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Newer
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      )}

      {/* Content area */}
      <div className="space-y-3 px-4 py-3">
        {/* Original */}
        <TextContentView
          type="original"
          title="Original"
          content={comparison.original}
        />
        <TextMetricsBar text={comparison.original} />

        {/* Suggested */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              Suggested version
            </h3>

            {/* View mode toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
              <button
                onClick={() => onViewModeChange("diff")}
                disabled={isBusy}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  viewMode === "diff"
                    ? "bg-white text-brand-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Changes
              </button>
              <button
                onClick={() => onViewModeChange("full")}
                disabled={isBusy}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  viewMode === "full"
                    ? "bg-white text-brand-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Full
              </button>
            </div>
          </div>

          {/* Suggestion selector */}
          {comparison.suggestions.length > 1 && (
            <div className="mb-2">
              <label htmlFor="suggestion-select" className="sr-only">
                Select suggestion version
              </label>
              <select
                id="suggestion-select"
                aria-label="Select suggestion version"
                value={comparison.selectedSuggestionIndex}
                onChange={(e) => onSelectedSuggestionChange(Number(e.target.value))}
                disabled={isBusy}
                className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white text-gray-800 focus:outline-none focus:border-brand-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.12)] transition-all select-custom disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {comparison.suggestions.map((suggestion, index) => (
                  <option key={index} value={index}>
                    {suggestion.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {viewMode === "diff" ? (
            <InlineDiffView
              ref={diffRef}
              original={comparison.original}
              suggested={selectedSuggestion}
            />
          ) : (
            <TextContentView
              type="suggested"
              title=""
              content={selectedSuggestion}
              isMarkdown={true}
            />
          )}

          <TextMetricsBar text={selectedSuggestion} isMarkdown={true} onCopy={onCopyReport} />
          <ExplainChangesView
            original={comparison.original}
            suggested={selectedSuggestion}
            cache={explanationCache}
          />
        </div>
      </div>

      {/* Track changes + Insert comment options */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-t border-gray-100">
        <label
          className={`flex items-center gap-2 ${isBusy ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          title="Apply changes as Word Tracked Changes"
        >
          <div className="relative flex-shrink-0">
            <input
              type="checkbox"
              checked={useTrackChanges}
              onChange={(e) => onUseTrackChangesChange(e.target.checked)}
              disabled={isBusy}
              aria-label="Apply changes as Word Tracked Changes"
              className="peer sr-only"
            />
            <div className="w-7 h-4 bg-gray-200 rounded-full peer-checked:bg-brand-500 transition-colors" />
            <div className="pointer-events-none absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-all peer-checked:translate-x-3" />
          </div>
          <span className="text-xs text-gray-600">Track changes</span>
        </label>
        {useTrackChanges && !trackChangesSupported && (
          <span className="text-[10px] text-amber-500 flex items-center gap-1">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
            Not supported
          </span>
        )}
        <label
          className={`flex items-center gap-2 ${isBusy ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          title="Insert a Word margin comment summarising this edit after applying"
        >
          <div className="relative flex-shrink-0">
            <input
              type="checkbox"
              checked={insertReviewComment}
              onChange={(e) => onInsertReviewCommentChange(e.target.checked)}
              disabled={isBusy}
              aria-label="Insert review comment after applying"
              className="peer sr-only"
            />
            <div className="w-7 h-4 bg-gray-200 rounded-full peer-checked:bg-brand-500 transition-colors" />
            <div className="pointer-events-none absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-all peer-checked:translate-x-3" />
          </div>
          <span className="text-xs text-gray-600">Insert comment</span>
        </label>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 px-4 py-3 border-t border-gray-100">
        <button
          onClick={onDiscard}
          disabled={isBusy}
          aria-label="Discard changes (Escape)"
          className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-brand-600 border border-brand-200 rounded-xl hover:bg-brand-50 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 transition-all duration-150"
        >
          {comparisonAction === 'discard' ? <Spinner /> : null}
          Discard
        </button>
        <button
          onClick={onApply}
          disabled={isBusy}
          aria-label={insertReviewComment ? "Add comment to document" : "Apply changes to document"}
          style={{ background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)" }}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white rounded-xl shadow-[0_4px_20px_-4px_rgba(59,130,246,0.35)] hover:shadow-[0_8px_28px_-6px_rgba(59,130,246,0.45)] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:active:scale-100 transition-all duration-150"
        >
          {comparisonAction === 'apply' ? <Spinner /> : null}
          {insertReviewComment ? "Add Comment" : "Apply Changes"}
        </button>
      </div>
    </div>
  );
};

export default ComparisonPanel;
